import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Table, Text } from '@factorialco/deprecated-design-system'
import { SaleInvoiceDetailFragment } from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { formatDate } from 'modules/finance/lib/salesInvoiceUtils'

import {
  RelatedFinancialDocumentsRow,
  RelatedFinancialDocumentsRowProps,
} from './_RelatedFinancialDocumentsRow'

interface RelatedFinancialDocumentsProps {
  locale: string
  saleInvoice: SaleInvoiceDetailFragment
  relatedFinancialDocumentPath: (relatedInvoiceId: number) => string
}

const RelatedFinancialDocumentsHeaderCell = ({ value }: { value: string }) => (
  <Text.Normal size="200" weight="medium">
    {value}
  </Text.Normal>
)

function buildRelatedFinancialDocumentRows({
  saleInvoice,
  locale,
  onNavigate,
}: {
  saleInvoice: SaleInvoiceDetailFragment
  locale: string
  onNavigate: (invoiceId: number) => void
}): RelatedFinancialDocumentsRowProps[] {
  const parentFinancialDocument = saleInvoice.parentFinancialDocument
  const childFinancialDocument = saleInvoice.childFinancialDocument

  const rows: RelatedFinancialDocumentsRowProps[] = []

  if (childFinancialDocument) {
    rows.push({
      number: childFinancialDocument.documentNumber || '-',
      amount: formattedAmount({
        amountCents: childFinancialDocument.totalAmountCents,
        currency: childFinancialDocument.currency,
        locale,
        emptyValue: '-',
      }),
      operationDate: formatDate(childFinancialDocument.documentDate),
      issueDate: formatDate(childFinancialDocument.validatedAt),
      onClick: () => {
        onNavigate(childFinancialDocument.id)
      },
      validated: !!childFinancialDocument.validatedAt,
      registered: !!childFinancialDocument.registerSaleAt,
    })
  }

  rows.push({
    number: saleInvoice.documentNumber || '-',
    amount: formattedAmount({
      amountCents: saleInvoice.totalAmountCents,
      currency: saleInvoice.currency,
      locale,
      emptyValue: '-',
    }),
    operationDate: formatDate(saleInvoice.documentDate),
    issueDate: formatDate(saleInvoice.validatedAt),
    selected: true,
    validated: !!saleInvoice.validatedAt,
    registered: !!saleInvoice.registerSaleAt,
  })

  if (parentFinancialDocument) {
    rows.push({
      number: parentFinancialDocument.documentNumber || '-',
      amount: formattedAmount({
        amountCents: parentFinancialDocument.totalAmountCents,
        currency: parentFinancialDocument.currency,
        locale,
        emptyValue: '-',
      }),
      operationDate: formatDate(parentFinancialDocument.documentDate),
      issueDate: formatDate(parentFinancialDocument.validatedAt),
      onClick: () => {
        onNavigate(parentFinancialDocument.id)
      },
      validated: !!parentFinancialDocument.validatedAt,
      registered: !!parentFinancialDocument.registerSaleAt,
    })
  }

  return rows
}

const RelatedFinancialDocuments = ({
  locale,
  saleInvoice,
  relatedFinancialDocumentPath,
}: RelatedFinancialDocumentsProps) => {
  const navigate = useNavigate()

  const handleNavigate = useCallback(
    (invoiceId: number) => {
      navigate(relatedFinancialDocumentPath(invoiceId))
    },
    [navigate, relatedFinancialDocumentPath]
  )

  const relatedFinancialDocuments = useUnsafeMemo(() => {
    return buildRelatedFinancialDocumentRows({
      saleInvoice,
      locale,
      onNavigate: handleNavigate,
    })
  }, [saleInvoice, locale, handleNavigate])

  const headers = [
    i18n.t('finance.sale_invoices.detail.related_financial_documents.number'),
    i18n.t('finance.sale_invoices.detail.line_items.amount'),
    i18n.t('finance.sale_invoices.detail.related_financial_documents.operation_date'),
    i18n.t('finance.sale_invoices.detail.related_financial_documents.issue_date'),
    i18n.t('finance.sale_invoices.datatable.fields.status'),
  ]

  return (
    <Table fullWidth style={{ overflowY: 'auto', height: 'auto' }} autoLayout>
      <Table.Head>
        <Table.Row>
          {headers.map((header, index) => (
            <Table.HeadCell
              key={index}
              paddingX={index === 0 ? 's4' : 's24'}
              ellipsis
              background="grey000White"
            >
              <Box style={index === 0 ? { paddingLeft: '20px' } : { paddingRight: '20px' }}>
                <RelatedFinancialDocumentsHeaderCell value={header} />
              </Box>
            </Table.HeadCell>
          ))}
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {relatedFinancialDocuments.map((row) => (
          <RelatedFinancialDocumentsRow key={row.number} {...row} />
        ))}
      </Table.Body>
    </Table>
  )
}

export default RelatedFinancialDocuments
